// src/pages/Components/Keywords.js
import axios from "axios";
import {
  apiRequestBodyBuilder,
  formatSegmentData,
} from "../../config/tableHelper";

import {
  KeywordCompareColumns,
  KeywordDefaultColumns,
  KeywordGroupColumns,
  KeywordSegmentClickTypeColumns,
  KeywordSegmentDateColumns,
  KeywordSegmentDateGroupColumns,
  KeywordSegmentDeviceColumns,
  KeywordSegmentNetworkColumns,
  KeywordSegmentTopVsOtherColumns,
} from "./KeywordsColumns";

const API_BASE_URL = "https://api.confidanto.com";

// --- Helpers ---

const updateGroupColumnTitle = (
  columns,
  currentGroupType,
  suffix = "Group"
) => {
  if (!currentGroupType) return columns;

  return columns.map((col) => {
    if (col.key === "group_name") {
      return {
        ...col,
        title: `${
          currentGroupType.charAt(0).toUpperCase() + currentGroupType.slice(1)
        } ${suffix}`,
      };
    }
    return col;
  });
};

const updateSegmentDateColumns = (columns, groupBy) => {
  let newCols = columns.map((item) => {
    if (["date", "week", "month", "segment"].includes(item.key)) {
      return {
        ...item,
        title: groupBy.charAt(0).toUpperCase() + groupBy.slice(1).toLowerCase(),
      };
    }
    return item;
  });

  if (groupBy === "date") {
    newCols = newCols.map((item) =>
      item.key === "day_name" ? { ...item, visible: true } : item
    );
  }
  return newCols;
};

// --- API Functions ---

// Default
export const fetchKeywordData = async (
  state,
  setColumns,
  setData,
  setTotal
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state);

    const response = await axios.post(
      `${API_BASE_URL}/keywords/datewise-data`,
      requestBody
    );

    setData(response.data);
    setTotal({});
    setColumns(KeywordDefaultColumns);
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

// Date
export const fetchKeywordCompareData = async (
  state,
  setColumns,
  setData,
  setTotal
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state);

    const response = await axios.post(
      `${API_BASE_URL}/keywords/datewise-data`,
      requestBody
    );

    setData(response.data);
    setTotal({});
    setColumns(KeywordDefaultColumns);
  } catch (error) {
    console.error("Error fetching compare data:", error);
  }
};

// Groups
export const fetchKeywordGroupsData = async (
  state,
  setColumns,
  setData,
  setTotal,
  selectedGroupIds,
  currentGroupType
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state, {
      group_ids: selectedGroupIds,
      type: currentGroupType,
    });

    const response = await axios.post(
      `${API_BASE_URL}/keyword-group/datewise-data`,
      requestBody
    );

    const groupData = response.data.map((item) => ({ ...item }));
    setData(groupData);

    const newCols = updateGroupColumnTitle(
      KeywordGroupColumns,
      currentGroupType,
      "Group"
    );

    setColumns(newCols);
    setTotal({});
  } catch (err) {
    console.error("Keyword Groups Total Error", err);
  }
};

// Segments
export const fetchKeywordSegmentDeviceData = async (
  state,
  setColumns,
  setData,
  setTotal
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state);
    const response = await axios.post(
      `${API_BASE_URL}/keywords/device`,
      requestBody
    );

    setTotal({});
    setColumns(KeywordSegmentDeviceColumns);
    setData(response.data);
  } catch (err) {
    console.error("Keyword Segment Device Error: ", err);
  }
};

export const fetchKeywordSegmentTopVsOtherData = async (
  state,
  setColumns,
  setData,
  setTotal
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state);
    const response = await axios.post(
      `${API_BASE_URL}/keywords/top-vs-other`,
      requestBody
    );

    setTotal({});
    setColumns(KeywordSegmentTopVsOtherColumns);
    setData(response.data);
  } catch (err) {
    console.error("Keyword Segment TopVsOther Error: ", err);
  }
};

export const fetchKeywordSegmentClickTypeData = async (
  state,
  setColumns,
  setData,
  setTotal
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state);
    const response = await axios.post(
      `${API_BASE_URL}/keywords/click-type`,
      requestBody
    );

    setTotal({});
    setColumns(KeywordSegmentClickTypeColumns);
    setData(response.data);
  } catch (err) {
    console.error("Keyword Segment ClickType Error: ", err);
  }
};

export const fetchKeywordSegmentNetworkData = async (
  state,
  setColumns,
  setData,
  setTotal
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state);
    const response = await axios.post(
      `${API_BASE_URL}/keywords/network`,
      requestBody
    );

    setTotal({});
    setColumns(KeywordSegmentNetworkColumns);
    setData(response.data);
  } catch (err) {
    console.error("Keyword Segment Network Error: ", err);
  }
};

export const fetchKeywordSegmentDateGroupData = async (
  state,
  setColumns,
  setData,
  setTotal,
  groupBy,
  groupIds,
  currentGroupType
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state, {
      group_by: groupBy,
      group_ids: groupIds,
    });

    const response = await axios.post(
      `${API_BASE_URL}/keyword-group/segment-data`,
      requestBody
    );

    setTotal({});

    // Update Group Titles
    let newCols = updateGroupColumnTitle(
      KeywordSegmentDateGroupColumns,
      currentGroupType,
      "Groups"
    );

    // Update Date/Segment Titles
    newCols = updateSegmentDateColumns(newCols, groupBy);

    setColumns(newCols);
    setData(formatSegmentData(response.data, groupBy));
  } catch (err) {
    console.error("Keyword Segment Date Group Error: ", err);
  }
};

export const fetchKeywordSegmentDateData = async (
  state,
  setColumns,
  setData,
  setTotal,
  groupBy
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state, {
      group_by: groupBy,
    });

    const response = await axios.post(
      `${API_BASE_URL}/keywords/segment-data`,
      requestBody
    );

    setTotal({});

    const newCols = updateSegmentDateColumns(
      KeywordSegmentDateColumns,
      groupBy
    );

    setColumns(newCols);
    setData(formatSegmentData(response.data, groupBy));
  } catch (err) {
    console.error("Keyword Segment Date Error: ", err);
  }
};
